import { makeStyles } from '@mui/styles';

import {
  MOO_SIDEBAR_RESIZING_CLASS,
  MOO_SIDEBAR_WIDTH_CSS_VAR,
} from './components/ask-moo/v2/sidebar-contract';
import {
  LAYOUT_TRANSITION_DURATION,
  RAIL_WIDTH_REM,
  SIDEBAR_WIDTH_REM,
  TILE_GAP_REM,
  TILE_RADIUS_REM,
  TILE_SHADOW,
  TILE_SHADOW_DARK,
} from './components/panels/panelLayout';

const useGlobalStyles = makeStyles(globalStyles);

const GlobalStyles = () => {
  // Sets global style overrides
  useGlobalStyles();

  return null;
};

export default GlobalStyles;

function globalStyles({ palette, shape, ...theme }: any) {
  const primaryText = palette.text.primary;
  const secondaryText = palette.text.secondary;

  return {
    '@global': {
      html: {
        fontSize: 12,

        '@media (min-width: 1620px)': {
          fontSize: 14,
        },

        '@media (min-width: 1920px)': {
          fontSize: 16,
        },
      },

      body: {
        minHeight: '100%',
        margin: 0,
        overflow: 'auto',
        background: palette.background.default,
        backgroundImage:
          'url(/images/public-background-overlay.png), url(/images/public-background-2k.jpg)',
        backgroundSize: 'cover, cover',
        backgroundRepeat: 'no-repeat, repeat',
        color: primaryText,
        fontFamily: theme.typography.fontFamily,
        '-webkit-font-smoothing': 'antialiased',
      },

      '.oswald': {
        fontFamily: 'Oswald',
      },

      '.titillium-reg, .titillium-light': {
        fontFamily: theme.config.fonts.titillium,
        fontWeight: theme.config.weights.normal,
      },

      '.titillium-light': {
        fontWeight: theme.config.weights.light,
      },

      img: {
        maxWidth: '100%',
      },

      a: {
        color: secondaryText,
        textDecoration: 'none',
      },

      h1: {
        fontSize: theme.config.textSizes.canon,
        fontWeight: theme.typography.fontWeightLight,
        color: palette.text.secondary,
        '-webkit-font-smoothing': 'antialiased',
        fontSmooth: 'always',
      },

      h2: {
        fontSize: theme.config.textSizes.paragon,
        color: palette.text.text8,
      },
      '.nonpareil-text': {
        fontSize: theme.config.textSizes.nonpareil,
      },

      '.canon-text': {
        fontSize: theme.config.textSizes.canon,
      },

      '.paragon-text': {
        fontSize: theme.config.textSizes.paragon,
      },

      '.great-primer-text': {
        fontSize: theme.config.textSizes.greatPrimer,
      },

      '.primer-text': {
        fontSize: theme.config.textSizes.primer,
      },

      '.tertia-prime-text': {
        fontSize: theme.config.textSizes.tertiaPrime,
      },

      '.tertia-text': {
        fontSize: theme.config.textSizes.tertia,
      },

      '.normal-text': {
        fontSize: theme.config.textSizes.normal,
      },

      '.petite-text': {
        fontSize: theme.config.textSizes.petite,
      },

      '.minion-text': {
        fontSize: theme.config.textSizes.minion,
      },

      '.contrast-text': {
        color: palette.primary.contrastText,
      },

      '.blue-text': {
        color: palette.common.cowbellBlue,

        'a&': {
          fontWeight: 600,
          textDecoration: 'underline',
        },
      },

      '.green-text': {
        color: palette.common.cowbellAccent,

        'a&': {
          fontWeight: 600,
          textDecoration: 'underline',
        },
      },

      '.yellow-text': {
        color: palette.common.yellow[500],
      },

      '.orange-text': {
        color: '#ffcc58',
      },

      '.error-text': {
        color: palette.common.tomato,
      },

      // used for global color for react-highlighter
      '.highlight': {
        background: palette.common.cowbellAccent,
        color: palette.primary.contrastText,
      },

      '.text-uppercase': {
        textTransform: 'uppercase',
      },

      '.card-heading': {
        marginBottom: 5,
        position: 'relative',
        fontSize: '1rem',
        textTransform: 'uppercase',

        '&:first-of-type': {
          marginTop: 0,
        },

        // test change for a force CI commit
        '&:after': {
          content: "''",
          width: '100%',
          height: 1,
          position: 'absolute',
          bottom: 0,
          left: 0,
          backgroundColor: palette.text.primary,
        },
      },

      '.card-heading-bg-blue': {
        padding: '1rem',
        paddingBottom: 0,
        margin: '0',
        color:
          palette.mode === 'dark'
            ? palette.common.surfaceDisabled
            : palette.common.outerSpace,
        fontWeight: 600,
        fontSize: '1.3333rem',
        lineHeight: '24px',
        marginLeft: '-16px',
        marginRight: '-16px',
        marginTop: '-16px',
        borderTopRightRadius: '5px',
        borderTopLeftRadius: '5px',

        '&:after': {
          display: 'block',
          marginTop: '4px',
          width: '100%',
          content: "''",
          height: '1px',
          backgroundColor: palette.mode === 'dark' ? '#9aa0ae' : '#BDC3CD',
        },
      },

      '.company': {
        fontWeight: theme.typography.fontWeightRegular,
        '-webkit-font-smoothing': 'antialiased',
        fontSmooth: 'always',
      },

      '.flex--center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      '.flex--spaced': {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',

        '&.align-baseline': {
          alignItems: 'baseline',
        },

        '&.align-start': {
          alignItems: 'flex-start',
        },

        '.action-bar > &': {
          marginBottom: '0.8333333333rem',
        },
      },

      '.view__header': {
        display: 'flex',
        padding: 0,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',

        '& > div': {
          display: 'flex',
          alignItems: 'center',
        },
        '& > h1': {
          color: palette.background.paper,
          fontSize: '18px',
        },
      },

      // A column, so the in-flow "Not Production" strip takes its height out
      // of the page instead of pushing a full-height root container past the
      // bottom of the window.
      '.main-root-wrapper': {
        width: `calc(100% - var(${MOO_SIDEBAR_WIDTH_CSS_VAR}, 0px))`,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: palette.background.default,
        // Narrows while the Ask Moo sidebar slides in, at the same pace.
        transition: 'width var(--layout-transition-duration) ease-out',
      },

      // A drag resize sets the width every mousemove; a transition would make
      // the page trail behind the handle.
      [`body.${MOO_SIDEBAR_RESIZING_CLASS} .main-root-wrapper`]: {
        transition: 'none',
      },

      // The product's gutter, published as a custom property so every layout
      // reads one value -- and so it can be retuned in one place (or live in
      // devtools) without hunting through layouts.
      ':root': {
        '--tile-gutter': TILE_GAP_REM,
        '--tile-radius': TILE_RADIUS_REM,
        '--sidebar-width': SIDEBAR_WIDTH_REM,
        '--layout-transition-duration': LAYOUT_TRANSITION_DURATION,
        // The tile background, so tiles drawn outside MUI's Paper pick up the same colour in
        // both schemes instead of the page background they sit on.
        '--tile-surface': palette.background.paper,
        // The page colour the layout wrappers paint, for a page that mounts with no wrapper behind it.
        '--page-background': palette.background.default,
        '--tile-shadow':
          palette.mode === 'dark' ? TILE_SHADOW_DARK : TILE_SHADOW,
      },

      '.main-root-container': {
        display: 'flex',
        width: '100%',
        flex: '1 0 auto',
        background: palette.background.default,

        // The top clears the fixed header by its measured height (published
        // by MainHeader; 5.4rem until it has rendered) plus one tile gutter, so
        // page titles and controls don't sit flush under the header; the sides
        // are the tile gutter, so the frame around the dashboard matches the
        // gaps inside it.
        [theme.breakpoints.up('md')]: {
          padding:
            'calc(var(--console-header-h, 5.4rem) + var(--tile-gutter)) var(--tile-gutter) 0 var(--tile-gutter)',
        },

        [theme.breakpoints.up('lg')]: {
          padding:
            'calc(var(--console-header-h, 5.4rem) + var(--tile-gutter)) var(--tile-gutter) 0 var(--tile-gutter)',
        },
      },

      '.main-content-container': {
        flexBasis: '100%',
        flexGrow: 1,
        padding: '.5rem 0 0 var(--tile-gutter)',
        background: palette.background.default,

        '& h1': {
          color: palette.primary.title,
        },
      },

      '.main-content': {
        display: 'flex',
        flex: 1,
        flexGrow: 1,
        flexBasis: '100%',
        flexDirection: 'column',
        maxWidth: 'calc(100% - var(--sidebar-width))',
        // With the rail beside it (`.sidebar-container`), so the two share one duration and curve; both are nil under
        // reduced motion.
        transition:
          'max-width var(--motion-duration-panel) var(--motion-ease-standard)',
        marginBottom: 'var(--tile-gutter)',
        [theme.breakpoints.down('md')]: {
          maxWidth: 'calc(100% - 0.833333333333333rem)',
        },

        '& > *': {
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'flex-start',
        },
      },

      // The main area's width is capped to the *expanded* sidebar width above.
      // When the Cowbell sidebar collapses to the icon rail, relax that cap
      // (via the sibling rail modifier on .sidebar-container) so the content
      // widens into the freed space instead of just sliding left and leaving a
      // dead band on the right. Both sides read the same RAIL_WIDTH_REM, so the
      // cap cannot drift from the rail it is compensating for.
      '.sidebar-container.is-rail-collapsed ~ .main-content': {
        // Only widen into the freed rail space at md+, where the collapse-to-
        // rail actually exists. Below md the sidebar is an off-canvas drawer, so
        // a persisted collapsed-rail preference must NOT reserve the rail's
        // width — leave that to the mobile `.main-content` rule so there's no
        // dead band.
        [theme.breakpoints.up('md')]: {
          maxWidth: `calc(100% - ${RAIL_WIDTH_REM})`,
        },
      },

      '.sidebar-container': {
        width: 'var(--sidebar-width)',
        transition:
          'width var(--motion-duration-panel) var(--motion-ease-standard)',

        // The collapse-to-icon rail. Scoped to this modifier so the expanded
        // width is untouched. In rem, not the px it used to be: the app
        // re-bases its root font at wider viewports, so a pinned px rail stops
        // scaling with the icons sitting in it.
        '&.is-rail-collapsed': {
          width: RAIL_WIDTH_REM,
        },

        [theme.breakpoints.down('md')]: {
          width: 'auto',
          // height: 'auto',
        },
      },

      '.footer': {
        zIndex: 10,
        textAlign: 'center',
        fontSize: '1rem',
        display: 'flex',
        flexDirection: 'column',
        height: '7rem',
        bottom: '-2rem',
        position: 'fixed',
        paddingLeft: '4.166666666666667rem',
        color: palette.mode === 'light' ? '#e4e8ed' : '#47474d',
      },

      '.select--button': {
        background: palette.background.default,
        border: `1px solid ${palette.common.lightBlueGrey}`,
        borderRadius: shape.borderRadius,
        color: palette.text.primary,
        fontSize: theme.typography.fontSize,

        '&:before': {
          display: 'none',
        },

        '&:after': {
          display: 'none',
        },
      },

      '.text-center': {
        textAlign: 'center',
      },

      '.text-right': {
        textAlign: 'right',
      },

      '.align-left': {
        justifyContent: 'flex-start',
      },

      '.align-right': {
        justifyContent: 'flex-end',
      },

      '.align-center': {
        justifyContent: 'center',
      },

      '.table-row__expandable:hover': {
        cursor: 'pointer',
      },

      '.table-heading': {
        marginBottom: 0,
        fontSize: theme.config.textSizes.paragon,
      },

      '.is-loading.shimmer': {
        position: 'relative',
        overflowX: 'hidden',
        background: palette.background.default,

        '&:before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: '0',
          left: '0',
          backgroundColor: palette.background.default,
          background: `linear-gradient(to right, transparent 1%, ${palette.tables.shimmer} 18%, transparent 40%)`,
          transform: 'translateX(-50%)',

          animationDuration: '1.75s',
          animationFillMode: 'forwards',
          animationIterationCount: 'infinite',
          animationName: 'block-shimmer',
        },

        '& > *': {
          opacity: 0,
        },
      },

      '.input-group': {
        marginTop: 15,
      },

      '.or-divider': {
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-evenly',

        '&:before': {
          content: '""',
          width: '45%',
          height: 1,
          background: palette.text.primary,
          marginRight: 10,
        },

        '&:after': {
          content: '""',
          width: '45%',
          height: 1,
          background: palette.text.primary,
          marginLeft: 10,
        },
      },

      '.accordion-header__checkbox': {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
      },

      '.accordion-header__checkbox__label': {
        margin: 0,
      },
      '.cursor--pointer': {
        '&:hover': {
          cursor: 'pointer',
        },
      },
      '.hover--link': {
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      '.mtn': {
        marginTop: 0,
      },

      '.man': {
        margin: 0,
      },

      '.mtn-login': {
        marginBottom: 20,
        '& label': {
          color: theme.config.colors.almostWhite,
        },
      },

      // The page's own scrollbar. The document root carries the login wallpaper, which showed
      // through its transparent track, brightest at the top right where the fixed header ends.
      // The bar is painted instead: the header's colour beside the header, the page's ground
      // below it. Its track starts below the header, so the thumb never runs alongside it.
      // Chrome takes the viewport's scrollbar from body as well as html, so both are named.
      'html, body': {
        '&::-webkit-scrollbar': {
          background: `linear-gradient(${palette.background.paper} var(--console-header-h, 0rem), ${palette.background.default} var(--console-header-h, 0rem))`,
        },
        '&::-webkit-scrollbar-track': {
          marginTop: 'var(--console-header-h, 0rem)',
        },
      },

      // A dev-express table scrolls inside its white Paper, and the gaps between its rows are each
      // cell's bottom border in the ground colour (MuiTableCell.body in _base.overrides). The
      // scrollbar's track is transparent everywhere (index.css), so here the white Paper showed
      // through as a white column beside the rows. The track takes the ground colour the gaps
      // take, so the scrollbar sits in the same grey as the gaps.
      '.TableContainer-root': {
        '&::-webkit-scrollbar-track, &::-webkit-scrollbar-corner': {
          backgroundColor: palette.background.default,
        },
      },

      '.stats-bar': {
        display: 'flex',
        justifyContent: 'space-evenly',
        flexFlow: 'wrap',
        marginBottom: '1rem',
        padding: '1rem',
      },

      '.page-container': {
        height: '100%',

        '& .main-container': {
          backgroundColor: palette.background.lighter,
          padding: '2.1667rem 2.083rem',
          height: '100%',

          '& .header-container': {
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${palette.primary.modalBorder}`,
            paddingBottom: '2rem',

            '& .header-title': {
              fontSize: '2rem',
              fontWeight: 600,
              lineHeight: 1.13,
              margin: '0 !important',
            },
          },
        },
      },
    },
  };
}
